import { IonCol, IonList, IonRow } from '@ionic/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { IOption, IPNNVOption } from '../../../common/common.types'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import { IPHIComponent } from '../../../components/phi/phi.component'
import { getCardiacArrestSelector } from '../selectors'
import {
  saveCardiacArrest,
  setAedBy,
  setAedUse,
  setArrestPresent,
  setArrestPresentPNNV,
  setArrestResult,
  setCprBy,
  setFirstCprPNNV,
  setCprPriorEmsArrival,
  setCprType,
  setDTAls,
  setDTCirculation,
  setDTCpr,
  setDTDefib,
  setDTDisc,
  setDTVent,
  setDTWitnessed,
  setDefibBy,
  setDefibByPNNV,
  setDefibType,
  setDestRhythm,
  setDestRhythmPNNV,
  setDiscReason,
  setDiscReasonPNNV,
  setEtiology,
  setEtiologyPNNV,
  setFirstAedBy,
  setFirstAedByPNNV,
  setFirstCpr,
  setInitialRhythm,
  setInitialRhythmPNNV,
  setResuscitation,
  setReturnCirculation,
  setTherapeuticHypothermia,
  setUnkAls,
  setUnkCirculation,
  setUnkCpr,
  setUnkDefib,
  setUnkDisc,
  setUnkVent,
  setUnkWitnessed,
  setWhoWitnessed,
  setCprPriorEmsArrivalPNNV,
  setDTWitnessedPNNV,
} from '../store/ccHpi.actions'
import { getAllPNNVOptionsSelector, getCcHpiOptionsSelector } from '../../options/selectors'
import Calendar from '../../../components/calendar/Calendar.component'
import './cardiacArrest.content.css'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { showAlert } from '../../../components/alert/store/alert.actions'

const CardiacArrestContent: React.FC<IPHIComponent> = (props: IPHIComponent) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const ccHpiCardiacArrestState = useSelector(getCardiacArrestSelector)
  const {
    arrestPresent,
    arrestPresentPNNV,
    dtWitnessed,
    dtWitnessedPNNV,
    whoWitnessed,
    etiology,
    etiologyPNNV,
    therapeuticHypothermia,
    initialRhythm,
    initialRhythmPNNV,
    dtCpr,
    cprBy,
    firstCprPNNV,
    cprPriorEmsArrival,
    cprPriorEmsArrivalPNNV,
    firstCpr,
    cprType,
    resuscitation,
    aedUse,
    firstAedBy,
    firstAedByPNNV,
    aedBy,
    dtDefib,
    defibType,
    defibBy,
    defibByPNNV,
    dtAls,
    dtDisc,
    discReason,
    discReasonPNNV,
    dtCirculation,
    returnCirculation,
    dtVent,
    destRhythm,
    destRhythmPNNV,
    arrestResult,
    unkWitnessed,
    unkAls,
    unkCirculation,
    unkCpr,
    unkDefib,
    unkDisc,
    unkVent,
  } = ccHpiCardiacArrestState

  const ccHpiOptions = useSelector(getCcHpiOptionsSelector)
  const {
    arrestPresentOptions,
    whoWitnessedOptions,
    etiologyOptions,
    therapeuticHypothermiaOptions,
    initialRhythmOptions,
    cprPriorEmsArrivalOptions,
    firstCprOptions,
    cprByOptions,
    cprTypeOptions,
    resuscitationOptions,
    aedUseOptions,
    firstAedByOptions,
    aedByOptions,
    defibTypeOptions,
    defibByOptions,
    discReasonOptions,
    returnCirculationOptions,
    destRhythmOptions,
    arrestResultOptions,
  } = ccHpiOptions

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)

  const dtWitnessedPNNVOptions = allPNNVOptions?.['eArrest.14']

  const isApproximateSelected = (pnnValue: IPNNVOption | null) => {
    return pnnValue?.id === 8801029
  }

  const setArrestPresentOnChange = (option: IOption) => {
    dispatch(setArrestPresent(option))
  }

  const setArrestPresentPNNVOnChange = (pnnvOption: IPNNVOption) => {
    dispatch(setArrestPresentPNNV(pnnvOption))
  }

  const setWitnessedOnChange = (value: string | null) => {
    dispatch(setDTWitnessed(value))
  }

  const setDateWitnessedPNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (!dtWitnessed && isApproximateSelected(pnnvOption)) {
      const alertState: IAlertState = {
        header: '',
        message: 'To select approximate, Witnesses/Est. time of arrest is first required',
        buttons: [{ text: t('pnnv.ok') }],
      }
      dispatch(showAlert(alertState))
    } else {
      dispatch(setDTWitnessedPNNV(pnnvOption))
    }
    if (!isApproximateSelected(pnnvOption) && pnnvOption) dispatch(setDTWitnessed(''))
  }

  const setEtiologyOnChange = (option: IOption) => {
    dispatch(setEtiology(option))
  }

  const setEtiologyPNNVOnChange = (pnnvOption: IPNNVOption) => {
    dispatch(setEtiologyPNNV(pnnvOption))
  }

  const setTherapeuticHypothermiaOnChange = (option: IOption) => {
    dispatch(setTherapeuticHypothermia(option))
  }

  const setInitialRhythmOnChange = (option: IOption) => {
    dispatch(setInitialRhythm(option))
  }

  const setInitialRhythmPNNVOnChange = (pnnvOption: IPNNVOption) => {
    dispatch(setInitialRhythmPNNV(pnnvOption))
  }

  const setCprStartedOnChange = (value: string | null) => {
    dispatch(setDTCpr(value))
  }

  const setCprPriorEmsArrivalOnChange = (value: string | null) => {
    dispatch(setCprPriorEmsArrival(value))
  }

  const setCprPriorEmsArrivalPNNVOnChange = (value: IPNNVOption | null) => {
    dispatch(setCprPriorEmsArrivalPNNV(value))
  }

  const setFirstCprOnChange = (value: string | null) => {
    dispatch(setFirstCpr(value))
  }

  const setFirstCprPNNVOnChange = (value: IPNNVOption) => {
    dispatch(setFirstCprPNNV(value))
  }

  const setAedUseOnChange = (value: string | null) => {
    dispatch(setAedUse(value))
  }

  const setFirstAedByOnChange = (option: IOption) => {
    dispatch(setFirstAedBy(option))
  }

  const setFirstAedByPNNVOnChange = (pnnvOption: IPNNVOption) => {
    dispatch(setFirstAedByPNNV(pnnvOption))
  }

  const setDefibOnChange = (value: string | null) => {
    dispatch(setDTDefib(value))
  }

  const setAlsOnChange = (value: string | null) => {
    dispatch(setDTAls(value))
  }

  const setDiscOnChange = (value: string | null) => {
    dispatch(setDTDisc(value))
  }

  const setDefibTypeOnChange = (value: string | null) => {
    dispatch(setDefibType(value))
  }

  const setDefibByOnChange = (option: IOption) => {
    dispatch(setDefibBy(option))
  }

  const setDefibByPNNVOnChange = (pnnvOption: IPNNVOption) => {
    dispatch(setDefibByPNNV(pnnvOption))
  }

  const setDiscReasonOnChange = (option: IOption) => {
    dispatch(setDiscReason(option))
  }

  const setDiscReasonPNNVOnChange = (pnnvOption: IPNNVOption) => {
    dispatch(setDiscReasonPNNV(pnnvOption))
  }

  const setCirculationOnChange = (value: string | null) => {
    dispatch(setDTCirculation(value))
  }

  const setVentOnChange = (value: string | null) => {
    dispatch(setDTVent(value))
  }

  const setArrestResultOnChange = (value: string | null) => {
    dispatch(setArrestResult(value))
  }

  const setUnknownWitnessedChange = (value: number) => {
    dispatch(setUnkWitnessed(value))
    saveCardiacArrest()
  }

  const setUnkCprChange = (value: number) => {
    dispatch(setUnkCpr(value))
    saveCardiacArrest()
  }

  const setUnkDefibChange = (value: number) => {
    dispatch(setUnkDefib(value))
    saveCardiacArrest()
  }

  const setUnkAlsChange = (value: number) => {
    dispatch(setUnkAls(value))
    saveCardiacArrest()
  }

  const setUnkDiscChange = (value: number) => {
    dispatch(setUnkDisc(value))
    saveCardiacArrest()
  }

  const setUnkCirculationChange = (value: number) => {
    dispatch(setUnkCirculation(value))
    saveCardiacArrest()
  }

  const setUnkVentChange = (value: number) => {
    dispatch(setUnkVent(value))
    saveCardiacArrest()
  }

  return (
    <>
      <IonList id='cardiacArrest'>
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='arrestPresent'
              options={arrestPresentOptions}
              selectedOption={arrestPresent}
              listHeader={t('ccHpi.arrestPresent')}
              label={t('ccHpi.arrestPresent')}
              action={setArrestPresentOnChange}
              selectedPNNVOption={arrestPresentPNNV}
              pnnvAction={setArrestPresentPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        <Calendar
          dataTestID='dtWitnessed'
          selectedDate={dtWitnessed}
          action={setWitnessedOnChange}
          showToggle={true}
          toggleValue={unkWitnessed}
          toggleAction={setUnknownWitnessedChange}
          isStateAction={true}
          label={t('ccHpi.witnessedArrest')}
          pnnvOptions={dtWitnessedPNNVOptions}
          pnnvValue={dtWitnessedPNNV}
          pnnvAction={setDateWitnessedPNNVOnChange}
          pnnvListHeader={t('ccHpi.witnessedArrest')}
          useBothNormalAndPertinent={isApproximateSelected(dtWitnessedPNNV)}
        />

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='WhoWitnessed'
              listHeader={t('ccHpi.whoWitnessed')}
              options={whoWitnessedOptions}
              savedValues={whoWitnessed}
              action={setWhoWitnessed}
              callback={saveCardiacArrest}
              label={t('ccHpi.whoWitnessed')}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='etiology'
              options={etiologyOptions}
              selectedOption={etiology}
              listHeader={t('ccHpi.etiology')}
              label={t('ccHpi.etiology')}
              action={setEtiologyOnChange}
              selectedPNNVOption={etiologyPNNV}
              pnnvAction={setEtiologyPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='therapeuticHypothermia'
              options={therapeuticHypothermiaOptions}
              selectedOption={therapeuticHypothermia}
              listHeader={t('ccHpi.therapeuticHypothermia')}
              label={t('ccHpi.therapeuticHypothermia')}
              action={setTherapeuticHypothermiaOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='initialRhythm'
              options={initialRhythmOptions}
              selectedOption={initialRhythm}
              listHeader={t('ccHpi.initialRhythm')}
              label={t('ccHpi.initialRhythm')}
              action={setInitialRhythmOnChange}
              selectedPNNVOption={initialRhythmPNNV}
              pnnvAction={setInitialRhythmPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='cprPriorEmsArrival'
              options={cprPriorEmsArrivalOptions}
              selectedOption={cprPriorEmsArrival}
              listHeader={t('ccHpi.cprPriorEmsArrival')}
              label={t('ccHpi.cprPriorEmsArrival')}
              action={setCprPriorEmsArrivalOnChange}
              selectedPNNVOption={cprPriorEmsArrivalPNNV}
              pnnvAction={setCprPriorEmsArrivalPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='firstCpr'
              options={firstCprOptions}
              selectedOption={firstCpr}
              listHeader={t('ccHpi.firstCpr')}
              label={t('ccHpi.firstCpr')}
              action={setFirstCprOnChange}
              selectedPNNVOption={firstCprPNNV}
              pnnvAction={setFirstCprPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <Calendar
          dataTestID='dtCpr'
          selectedDate={dtCpr}
          action={setCprStartedOnChange}
          showToggle={true}
          toggleValue={unkCpr}
          toggleAction={setUnkCprChange}
          isStateAction={true}
          label={t('ccHpi.cprStarted')}
        />

        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='CprBy'
              listHeader={t('ccHpi.cprBy')}
              options={cprByOptions}
              savedValues={cprBy}
              action={setCprBy}
              callback={saveCardiacArrest}
              label={t('ccHpi.cprBy')}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='CprType'
              listHeader={t('ccHpi.cprType')}
              options={cprTypeOptions}
              savedValues={cprType}
              action={setCprType}
              callback={saveCardiacArrest}
              label={t('ccHpi.cprType')}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='Resuscitation'
              listHeader={t('ccHpi.resuscitation')}
              options={resuscitationOptions}
              savedValues={resuscitation}
              action={setResuscitation}
              callback={saveCardiacArrest}
              label={t('ccHpi.resuscitation')}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='aedUse'
              options={aedUseOptions}
              selectedOption={aedUse}
              listHeader={t('ccHpi.aedUse')}
              label={t('ccHpi.aedUse')}
              action={setAedUseOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='firstAedBy'
              options={firstAedByOptions}
              selectedOption={firstAedBy}
              listHeader={t('ccHpi.firstAedBy')}
              label={t('ccHpi.firstAedBy')}
              action={setFirstAedByOnChange}
              selectedPNNVOption={firstAedByPNNV}
              pnnvAction={setFirstAedByPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='AedBy'
              listHeader={t('ccHpi.aedBy')}
              options={aedByOptions}
              savedValues={aedBy}
              action={setAedBy}
              callback={saveCardiacArrest}
              label={t('ccHpi.aedBy')}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <Calendar
          dataTestID='dtDefib'
          selectedDate={dtDefib}
          action={setDefibOnChange}
          showToggle={true}
          toggleValue={unkDefib}
          toggleAction={setUnkDefibChange}
          isStateAction={true}
          label={t('ccHpi.firstDefib')}
        />

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='defibType'
              options={defibTypeOptions}
              selectedOption={defibType}
              listHeader={t('ccHpi.defibType')}
              label={t('ccHpi.defibType')}
              action={setDefibTypeOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='defibBy'
              options={defibByOptions}
              selectedOption={defibBy}
              listHeader={t('ccHpi.defibBy')}
              label={t('ccHpi.defibBy')}
              action={setDefibByOnChange}
              selectedPNNVOption={defibByPNNV}
              pnnvAction={setDefibByPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <Calendar
          dataTestID='dtAls'
          selectedDate={dtAls}
          action={setAlsOnChange}
          showToggle={true}
          toggleValue={unkAls}
          toggleAction={setUnkAlsChange}
          isStateAction={true}
          label={t('ccHpi.dtAls')}
        />

        <hr className='field-divider' />

        <Calendar
          dataTestID='dtDisc'
          selectedDate={dtDisc}
          action={setDiscOnChange}
          showToggle={true}
          toggleValue={unkDisc}
          toggleAction={setUnkDiscChange}
          isStateAction={true}
          label={t('ccHpi.dtDisc')}
        />

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='discReason'
              options={discReasonOptions}
              selectedOption={discReason}
              listHeader={t('ccHpi.discReason')}
              label={t('ccHpi.discReason')}
              action={setDiscReasonOnChange}
              selectedPNNVOption={discReasonPNNV}
              pnnvAction={setDiscReasonPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <Calendar
          dataTestID='dtCirculation'
          selectedDate={dtCirculation}
          action={setCirculationOnChange}
          showToggle={true}
          toggleValue={unkCirculation}
          toggleAction={setUnkCirculationChange}
          isStateAction={true}
          label={t('ccHpi.dtCirculation')}
        />

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='returnCirculation'
              listHeader={t('ccHpi.returnCirculation')}
              options={returnCirculationOptions}
              savedValues={returnCirculation}
              action={setReturnCirculation}
              callback={saveCardiacArrest}
              label={t('ccHpi.returnCirculation')}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <Calendar
          dataTestID='dtVent'
          selectedDate={dtVent}
          action={setVentOnChange}
          showToggle={true}
          toggleValue={unkVent}
          toggleAction={setUnkVentChange}
          isStateAction={true}
          label={t('ccHpi.dtVent')}
        />

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='destRhythm'
              listHeader={t('ccHpi.destRhythm')}
              options={destRhythmOptions}
              savedValues={destRhythm}
              action={setDestRhythm}
              callback={saveCardiacArrest}
              label={t('ccHpi.destRhythm')}
              savedPNNVValue={destRhythmPNNV}
              pnnvAction={setDestRhythmPNNV}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='arrestResult'
              options={arrestResultOptions}
              selectedOption={arrestResult}
              listHeader={t('ccHpi.arrestResult')}
              label={t('ccHpi.arrestResult')}
              action={setArrestResultOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
      </IonList>
    </>
  )
}

export default CardiacArrestContent
